import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";

const genUid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `it_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
const EMPTY_ITEM = { material_id: "", material_name: "", uom: "nos", qty: 1, avg_cost: 0, amount: 0 };
const newItem = () => ({ ...EMPTY_ITEM, _uid: genUid() });
const EMPTY = { project_id: "", date: new Date().toISOString().slice(0, 10), items: [ newItem() ], tower: "", floor: "", issued_to: "", notes: "" };

export default function MaterialIssues() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: materials = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const [projectFilter, setProjectFilter] = useState("");
  const { data: issues = [] } = useQuery({
    queryKey: ["material-issues", projectFilter],
    queryFn: async () => (await api.get("/material-issues", { params: projectFilter ? { project_id: projectFilter } : {} })).data,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const total = useMemo(() => form.items.reduce((s, i) => {
    const m = materials.find((x) => x.id === i.material_id);
    const cost = m?.avg_cost || 0;
    return s + (Number(i.qty) || 0) * cost;
  }, 0), [form.items, materials]);

  function setItem(idx, patch) {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      if (patch.material_id) {
        const m = materials.find((x) => x.id === patch.material_id);
        if (m) items[idx] = { ...items[idx], material_name: m.name, uom: m.uom || m.unit || "nos", avg_cost: m.avg_cost };
      }
      return { ...f, items };
    });
  }
  function addLine() { setForm((f) => ({ ...f, items: [...f.items, newItem()] })); }
  function delLine(idx) { setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) })); }

  async function save() {
    if (!form.project_id) return toast.error("Select a project");
    const items = form.items.filter((i) => i.material_id && i.qty > 0);
    if (!items.length) return toast.error("Add at least one material");
    try {
      await api.post("/material-issues", {
        ...form,
        items: items.map((i) => ({ ...i, qty: +i.qty })),
        floor: form.floor ? +form.floor : null,
      });
      toast.success("Material issued · stock reduced, expense booked");
      qc.invalidateQueries({ queryKey: ["material-issues"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      qc.invalidateQueries({ queryKey: ["dashboard-kpi"] });
      setOpen(false);
      setForm(EMPTY);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="material-issues-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Construction</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Material Issues</h1>
          <p className="text-sm text-zinc-500 mt-1">Issue material to a site — inventory drops, consumption expense books at moving-average cost.</p>
        </div>
        <div className="flex gap-2">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-64" data-testid="mi-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-issue-btn"><Plus size={14} className="mr-1" /> New Issue</Button></DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Issue Material to Site</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="md:col-span-2"><Label>Project / Site</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="mi-project"><SelectValue placeholder="Pick project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Issued To</Label><Input value={form.issued_to} onChange={(e) => setForm({ ...form, issued_to: e.target.value })} placeholder="Contractor / Engineer" data-testid="mi-issued-to" /></div>
                <div><Label>Tower</Label><Input value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} /></div>
                <div><Label>Floor</Label><Input type="number" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>

              <div className="mt-3 border border-zinc-200 rounded-md overflow-hidden">
                <div className="bg-zinc-50 px-3 py-2 flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-600">Items</div>
                  <Button size="sm" variant="outline" onClick={addLine}><Plus size={12} className="mr-1" /> Add line</Button>
                </div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-[45%]">Material</TableHead>
                    <TableHead>UoM</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Avg Cost</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead />
                  </TableRow></TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => {
                      const m = materials.find((x) => x.id === it.material_id);
                      const avail = m?.current_stock ?? 0;
                      const amount = (Number(it.qty) || 0) * (m?.avg_cost || 0);
                      const short = (Number(it.qty) || 0) > avail;
                      return (
                        <TableRow key={it._uid || `mi-${idx}`}>
                          <TableCell>
                            <Select value={it.material_id} onValueChange={(v) => setItem(idx, { material_id: v })}>
                              <SelectTrigger className="h-9" data-testid={`mi-item-${idx}-mat`}><SelectValue placeholder="Choose material" /></SelectTrigger>
                              <SelectContent>{materials.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.current_stock || 0} {x.uom || x.unit})</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-xs">{m?.uom || m?.unit || ""}</TableCell>
                          <TableCell className={`text-right font-mono ${short ? "text-red-600" : ""}`}>{avail}</TableCell>
                          <TableCell className="text-right"><Input type="number" className="h-9 text-right" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} data-testid={`mi-item-${idx}-qty`} /></TableCell>
                          <TableCell className="text-right font-mono">{inr(m?.avg_cost || 0)}</TableCell>
                          <TableCell className="text-right font-mono">{inr(amount)}</TableCell>
                          <TableCell><Button size="sm" variant="ghost" onClick={() => delLine(idx)} className="text-red-600"><Trash2 size={14} /></Button></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="bg-zinc-950 text-white px-4 py-3 font-mono text-sm flex justify-between">
                  <span className="opacity-70">Consumption Total (at moving-avg cost)</span>
                  <span className="text-emerald-300 font-semibold">{inr(total)}</span>
                </div>
              </div>

              <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-issue">Issue material</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Issue #</TableHead><TableHead>Date</TableHead>
              <TableHead>Issued To</TableHead><TableHead>Location</TableHead>
              <TableHead>Items</TableHead><TableHead className="text-right">Amount</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {issues.map((i) => (
                <TableRow key={i.id} className="data-row" data-testid={`issue-row-${i.issue_number}`}>
                  <TableCell className="font-mono text-xs font-semibold">{i.issue_number}</TableCell>
                  <TableCell className="text-xs">{fmtDate(i.date)}</TableCell>
                  <TableCell>{i.issued_to || "—"}</TableCell>
                  <TableCell className="text-xs">{[i.tower, i.wing, i.floor && `F${i.floor}`].filter(Boolean).join(" · ") || "—"}</TableCell>
                  <TableCell className="text-xs">{i.items?.map((x) => `${x.material_name} × ${x.qty}`).join(", ").slice(0, 80)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(i.total_amount)}</TableCell>
                </TableRow>
              ))}
              {!issues.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-16">No material issues yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
